import TaskItem from '@tiptap/extension-task-item'

// `tiptap-markdown` parses task lists via `markdown-it-task-lists`, which only
// recognises `[ ] X` (bracket, space, then content) — empty checkboxes
// (`- [ ]`) round-trip as plain bullets with literal `[ ]` text because the
// trailing space is stripped during inline tokenisation. We override
// TaskItem's markdown storage to also rescue those orphan items in
// `parse.updateDOM`, so an empty trailing checkbox stays a checkbox after
// the markdown round-trip.
//
// `tiptap-markdown` resolves a node's markdown spec by merging the extension's
// own `storage.markdown` over its built-in default. Providing one here
// replaces the default for taskItem; the serializer mirrors the upstream one.

const TASK_RE = /^\s*\[([ xX])\](?:\s+(.*))?$/s

interface MarkdownSerializerState {
  write(content: string): void
  renderContent(node: { attrs: { checked: boolean } }): void
}

interface TaskItemNode {
  attrs: { checked: boolean }
}

export const RobustTaskItem = TaskItem.extend({
  addStorage() {
    return {
      markdown: {
        serialize(state: MarkdownSerializerState, node: TaskItemNode) {
          const check = node.attrs.checked ? '[x]' : '[ ]'
          state.write(`${check} `)
          state.renderContent(node)
        },
        parse: {
          updateDOM(element: HTMLElement) {
            element.querySelectorAll('.task-list-item').forEach(item => {
              const input = item.querySelector('input')
              item.setAttribute('data-type', 'taskItem')
              if (input) {
                item.setAttribute('data-checked', String((input as HTMLInputElement).checked))
                input.remove()
              }
            })

            element.querySelectorAll('ul').forEach(ul => {
              const items = Array.from(ul.children).filter(el => el.tagName === 'LI')
              if (!items.length) return
              const orphans: { li: Element; p: Element | null; checked: boolean; rest: string }[] = []
              for (const li of items) {
                if (li.classList.contains('task-list-item')) continue
                const p = li.querySelector(':scope > p')
                const text = (p ? p.textContent : li.textContent) ?? ''
                const m = TASK_RE.exec(text)
                if (m) orphans.push({ li, p, checked: (m[1] ?? '').toLowerCase() === 'x', rest: m[2] ?? '' })
              }
              if (!orphans.length) return
              const alreadyTaskList = ul.classList.contains('contains-task-list')
              if (!alreadyTaskList && orphans.length !== items.length) return
              ul.classList.add('contains-task-list')
              ul.setAttribute('data-type', 'taskList')
              for (const { li, p, checked, rest } of orphans) {
                li.setAttribute('data-type', 'taskItem')
                li.setAttribute('data-checked', String(checked))
                li.classList.add('task-list-item')
                if (p) p.textContent = rest
                else li.textContent = rest
              }
            })
          },
        },
      },
    }
  },
})

export default RobustTaskItem
